import { useApolloClient } from '@apollo/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  useDeleteCertificationProgressMutation,
  useListCertificationProgressQuery,
  useUpsertCertificationProgressMutation,
  GetCertificationProgressDocument,
  ListCertificationProgressDocument,
} from '@/@generated/codegen/API';

import type { TypedCertificationProgress } from '@/types/graphql-extensions';

/**
 * Re-export TypedCertificationProgress for consumers.
 * This type extends the generated CertificationProgressFieldsFragment
 * with proper typing for the answers JSON field.
 */
export type { TypedCertificationProgress };

export const CERTIFICATION_PROGRESS_QUERY_KEYS = {
  byUserAndCase: (userId: string, caseId: string, masterclassId: string) =>
    [
      'certification_progress',
      'user',
      userId,
      'case',
      caseId,
      'masterclass',
      masterclassId,
    ] as const,
  bestPassed: (userId: string, caseId: string, masterclassId: string) =>
    [
      'certification_progress',
      'best_passed',
      'user',
      userId,
      'case',
      caseId,
      'masterclass',
      masterclassId,
    ] as const,
  byCase: (caseId: string, masterclassId: string) =>
    ['certification_progress', 'case', caseId, 'masterclass', masterclassId] as const,
};

export const useCertificationProgress = (
  userId: string | null,
  caseId: string | null,
  masterclassId: string | null
) => {
  const { data, loading, error, refetch } = useListCertificationProgressQuery({
    variables: {
      where: {
        userId: { equals: userId || '' },
        caseId: { equals: caseId || '' },
        masterclassId: { equals: masterclassId || '' },
      },
      orderByArgs: { orderByField: 'attemptNumber', direction: 'desc' },
    },
    skip: !userId || !caseId || !masterclassId,
  });

  // Map GraphQL data to typed interface with camelCase fields
  const progressData: TypedCertificationProgress[] | undefined =
    data?.listCertificationProgress?.edges.map((edge) => ({
      id: edge.node.id,
      userId: edge.node.userId,
      caseId: edge.node.caseId,
      masterclassId: edge.node.masterclassId,
      attemptNumber: edge.node.attemptNumber,
      currentQuestion: edge.node.currentQuestion,
      answers: (edge.node.answers as (number | null)[]) || [],
      score: edge.node.score,
      passed: edge.node.passed,
      startedAt: edge.node.startedAt,
      completedAt: edge.node.completedAt,
      updatedAt: edge.node.updatedAt,
    }));

  return {
    data: progressData,
    isLoading: loading,
    error: error,
    refetch,
  };
};

export const useBestPassedCertification = (
  userId: string | null,
  caseId: string | null,
  masterclassId: string | null
) => {
  const { data, loading, error, refetch } = useListCertificationProgressQuery({
    variables: {
      where: {
        userId: { equals: userId || '' },
        caseId: { equals: caseId || '' },
        masterclassId: { equals: masterclassId || '' },
        passed: { equals: true },
      },
      orderByArgs: { orderByField: 'score', direction: 'desc' },
      paginationArgs: { first: 1 },
    },
    skip: !userId || !caseId || !masterclassId,
  });

  // Map GraphQL data to typed interface with camelCase fields
  const bestProgress: TypedCertificationProgress | null = data?.listCertificationProgress?.edges[0]
    ? {
        id: data.listCertificationProgress.edges[0].node.id,
        userId: data.listCertificationProgress.edges[0].node.userId,
        caseId: data.listCertificationProgress.edges[0].node.caseId,
        masterclassId: data.listCertificationProgress.edges[0].node.masterclassId,
        attemptNumber: data.listCertificationProgress.edges[0].node.attemptNumber,
        currentQuestion: data.listCertificationProgress.edges[0].node.currentQuestion,
        answers: (data.listCertificationProgress.edges[0].node.answers as (number | null)[]) || [],
        score: data.listCertificationProgress.edges[0].node.score,
        passed: data.listCertificationProgress.edges[0].node.passed,
        startedAt: data.listCertificationProgress.edges[0].node.startedAt,
        completedAt: data.listCertificationProgress.edges[0].node.completedAt,
        updatedAt: data.listCertificationProgress.edges[0].node.updatedAt,
      }
    : null;

  return {
    data: bestProgress,
    isLoading: loading,
    error: error,
    refetch,
  };
};

export const useInsertCertificationProgress = () => {
  const queryClient = useQueryClient();
  const apolloClient = useApolloClient();
  const [upsertMutation] = useUpsertCertificationProgressMutation();

  return useMutation({
    mutationFn: async (data: {
      userId: string;
      caseId: string;
      masterclassId: string;
      attemptNumber: number;
      currentQuestion?: number;
      answers?: (number | null)[];
      score?: number;
      passed?: boolean;
      completedAt?: string;
    }) => {
      // Note: userId is derived from auth context by the backend
      const result = await upsertMutation({
        variables: {
          data: {
            caseId: data.caseId,
            masterclassId: data.masterclassId,
            attemptNumber: data.attemptNumber,
            currentQuestion: data.currentQuestion,
            answers: data.answers,
            score: data.score,
            passed: data.passed,
            completedAt: data.completedAt ? new Date(data.completedAt) : undefined,
          },
        },
      });

      if (result.errors) throw new Error(result.errors[0].message);
      if (!result.data?.upsertCertificationProgress) throw new Error('Failed to create progress');

      // Return camelCase typed data
      return {
        id: result.data.upsertCertificationProgress.id,
        userId: result.data.upsertCertificationProgress.userId,
        caseId: result.data.upsertCertificationProgress.caseId,
        masterclassId: result.data.upsertCertificationProgress.masterclassId,
        attemptNumber: result.data.upsertCertificationProgress.attemptNumber,
        currentQuestion: result.data.upsertCertificationProgress.currentQuestion,
        answers: (result.data.upsertCertificationProgress.answers as (number | null)[]) || [],
        score: result.data.upsertCertificationProgress.score,
        passed: result.data.upsertCertificationProgress.passed,
        startedAt: result.data.upsertCertificationProgress.startedAt,
        completedAt: result.data.upsertCertificationProgress.completedAt,
        updatedAt: result.data.upsertCertificationProgress.updatedAt,
      } as TypedCertificationProgress;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.byUserAndCase(
          data.userId,
          data.caseId,
          data.masterclassId
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.byCase(data.caseId, data.masterclassId),
      });
      void apolloClient.refetchQueries({
        include: ['listCertificationProgress'],
      });
    },
  });
};

export const useUpdateCertificationProgress = () => {
  const queryClient = useQueryClient();
  const apolloClient = useApolloClient();
  const [upsertMutation] = useUpsertCertificationProgressMutation();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: {
        currentQuestion?: number;
        answers?: (number | null)[];
        score?: number;
        passed?: boolean;
        completedAt?: string;
      };
    }) => {
      // First, query the existing record to get caseId, masterclassId and attemptNumber
      // (required for upsert since there's no direct update-by-id mutation)
      const existingResult = await apolloClient.query({
        query: GetCertificationProgressDocument,
        variables: { where: { id } },
        fetchPolicy: 'network-only',
      });

      const existing = existingResult.data?.getCertificationProgress;
      if (!existing) {
        throw new Error(`Certification progress with id ${id} not found`);
      }

      const result = await upsertMutation({
        variables: {
          data: {
            caseId: existing.caseId,
            masterclassId: existing.masterclassId,
            attemptNumber: existing.attemptNumber,
            currentQuestion: updates.currentQuestion,
            answers: updates.answers,
            score: updates.score,
            passed: updates.passed,
            completedAt: updates.completedAt ? new Date(updates.completedAt) : undefined,
          },
        },
      });

      if (result.errors) throw new Error(result.errors[0].message);
      if (!result.data?.upsertCertificationProgress) throw new Error('Failed to update progress');

      // Return camelCase typed data
      return {
        id: result.data.upsertCertificationProgress.id,
        userId: result.data.upsertCertificationProgress.userId,
        caseId: result.data.upsertCertificationProgress.caseId,
        masterclassId: result.data.upsertCertificationProgress.masterclassId,
        attemptNumber: result.data.upsertCertificationProgress.attemptNumber,
        currentQuestion: result.data.upsertCertificationProgress.currentQuestion,
        answers: (result.data.upsertCertificationProgress.answers as (number | null)[]) || [],
        score: result.data.upsertCertificationProgress.score,
        passed: result.data.upsertCertificationProgress.passed,
        startedAt: result.data.upsertCertificationProgress.startedAt,
        completedAt: result.data.upsertCertificationProgress.completedAt,
        updatedAt: result.data.upsertCertificationProgress.updatedAt,
      } as TypedCertificationProgress;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.byUserAndCase(
          data.userId,
          data.caseId,
          data.masterclassId
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.byCase(data.caseId, data.masterclassId),
      });
      void apolloClient.refetchQueries({
        include: ['listCertificationProgress'],
      });
    },
  });
};

export const useDeleteCertificationProgress = () => {
  const queryClient = useQueryClient();
  const apolloClient = useApolloClient();
  const [deleteMutation] = useDeleteCertificationProgressMutation();

  return useMutation({
    mutationFn: async ({
      userId,
      caseId,
      masterclassId,
    }: {
      userId: string;
      caseId: string;
      masterclassId: string;
    }) => {
      // First, get incomplete progress entries
      const listResult = await apolloClient.query({
        query: ListCertificationProgressDocument,
        variables: {
          where: {
            userId: { equals: userId },
            caseId: { equals: caseId },
            masterclassId: { equals: masterclassId },
            completedAt: { equals: null },
          },
        },
      });

      // Delete each incomplete entry
      const edges = listResult.data?.listCertificationProgress?.edges || [];
      for (const edge of edges) {
        const result = await deleteMutation({
          variables: {
            where: { id: edge.node.id },
          },
        });

        if (result.errors) throw new Error(result.errors[0].message);
      }
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.byUserAndCase(
          variables.userId,
          variables.caseId,
          variables.masterclassId
        ),
      });
      void apolloClient.refetchQueries({
        include: ['listCertificationProgress'],
      });
    },
  });
};
